/**
 * /payroll-l2/employees/:employeeId/rates — Multi-rate setup for a single employee
 *
 * Craft rates table, OT rule assignment, and retro pay preview.
 */
import { createFileRoute, Link } from "@tanstack/react-router";
import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Drawer,
  IconButton,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import { api } from "@/lib/api";
import type { EmployeeCraftRate, Craft, OTRuleSet } from "@/lib/types";
import { HelpHint } from "@/components/HelpHint";
import { MoneyField } from "@/components/MoneyField";
import { formatMoney } from "@/lib/money";

export const Route = createFileRoute("/_auth/payroll-l2/employees_/$employeeId/rates")({
  component: EmployeeRatesPage,
});

function fmtMoney(v: string | number) {
  return formatMoney(v);
}

interface RetroPreviewRow {
  work_date: string;
  hours: string;
  old_amount: string;
  new_amount: string;
  delta: string;
}

interface OTRuleAssignment {
  id: string;
  employee_id: string;
  ot_rule_set_id: string;
  effective_start: string;
}

function EmployeeRatesPage() {
  const { employeeId } = Route.useParams();
  const qc = useQueryClient();

  const [addRateOpen, setAddRateOpen] = useState(false);
  const [retroForm, setRetroForm] = useState({ old_rate: "", new_rate: "", effective_date: "" });
  const [retroPreview, setRetroPreview] = useState<RetroPreviewRow[] | null>(null);

  const [rateForm, setRateForm] = useState({
    craft_id: "",
    base_hourly_rate: "",
    fringe_hourly: "0",
    effective_start: new Date().toISOString().slice(0, 10),
    effective_end: "",
  });

  const { data: rates = [], isLoading: ratesLoading } = useQuery({
    queryKey: ["payroll-l2", "employee-rates", employeeId],
    queryFn: async () => {
      const res = await api(`/api/payroll-l2/employee-rates/${employeeId}`);
      return res as EmployeeCraftRate[];
    },
  });

  const { data: crafts = [] } = useQuery({
    queryKey: ["payroll-l2", "crafts"],
    queryFn: async () => {
      const res = await api(`/api/payroll-l2/crafts`);
      return res as Craft[];
    },
  });

  const { data: otRuleSets = [] } = useQuery({
    queryKey: ["payroll-l2", "ot-rule-sets"],
    queryFn: async () => {
      const res = await api(`/api/payroll-l2/ot-rule-sets`);
      return res as OTRuleSet[];
    },
  });

  const { data: otAssignment } = useQuery({
    queryKey: ["payroll-l2", "employee-ot-assignment", employeeId],
    queryFn: async () => {
      const res = await api(`/api/payroll-l2/employee-ot-assignment/${employeeId}`);
      return res as OTRuleAssignment | null;
    },
  });

  const createRateMut = useMutation({
    mutationFn: async () => {
      return api(`/api/payroll-l2/craft-rates`, {
        method: "POST",
        body: {
          employee_id: employeeId,
          ...rateForm,
          effective_end: rateForm.effective_end || null,
        },
      });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["payroll-l2", "employee-rates", employeeId] });
      setAddRateOpen(false);
      setRateForm({ craft_id: "", base_hourly_rate: "", fringe_hourly: "0", effective_start: new Date().toISOString().slice(0, 10), effective_end: "" });
    },
  });

  const deleteRateMut = useMutation({
    mutationFn: async (rateId: string) => {
      return api(`/api/payroll-l2/craft-rates/${rateId}`, { method: "DELETE" });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["payroll-l2", "employee-rates", employeeId] });
    },
  });

  const assignOTMut = useMutation({
    mutationFn: async (otRuleSetId: string) => {
      return api(`/api/payroll-l2/employee-ot-assignment`, {
        method: "POST",
        body: {
          employee_id: employeeId,
          ot_rule_set_id: otRuleSetId,
          effective_start: new Date().toISOString().slice(0, 10),
        },
      });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["payroll-l2", "employee-ot-assignment", employeeId] });
    },
  });

  const retroPreviewMut = useMutation({
    mutationFn: async () => {
      const params = new URLSearchParams({
        employee_id: employeeId,
        old_rate: retroForm.old_rate,
        new_rate: retroForm.new_rate,
        effective_date: retroForm.effective_date,
      });
      const res = await api(`/api/payroll-l2/retro-pay/preview?${params}`);
      return res as RetroPreviewRow[];
    },
    onSuccess: (data) => {
      setRetroPreview(data);
    },
  });

  const craftById: Record<string, Craft> = {};
  for (const c of crafts) craftById[c.id] = c;

  const otRuleById: Record<string, OTRuleSet> = {};
  for (const r of otRuleSets) otRuleById[r.id] = r;

  const retroTotal = retroPreview?.reduce((sum, r) => sum + parseFloat(r.delta), 0) ?? 0;

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
        <Link to="/payroll-l2/timesheet-feed">
          <Button startIcon={<ArrowBackIcon />} size="small">Back to Timesheet Feed</Button>
        </Link>
      </Box>

      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 2, mb: 3 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <Typography variant="h5" fontWeight={600}>Employee Rates</Typography>
          <Chip label={employeeId.slice(0, 8)} variant="outlined" size="small" sx={{ fontFamily: "monospace" }} />
        </Box>
        <HelpHint docPath="payroll-l2/employee-craft-rates.md" label="Craft rates help" />
      </Box>

      {/* Craft Rates */}
      <Paper variant="outlined" sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2 }}>
          <Typography variant="subtitle1" fontWeight={600}>Craft Rates</Typography>
          <Button variant="outlined" size="small" startIcon={<AddIcon />} onClick={() => setAddRateOpen(true)}>
            Add Rate
          </Button>
        </Box>

        {ratesLoading ? (
          <CircularProgress size={20} />
        ) : rates.length === 0 ? (
          <Alert severity="info">No craft rates configured.</Alert>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Craft</TableCell>
                <TableCell align="right">Base Rate</TableCell>
                <TableCell align="right">Fringe</TableCell>
                <TableCell>Effective Start</TableCell>
                <TableCell>Effective End</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {rates.map((rate) => (
                <TableRow key={rate.id}>
                  <TableCell>{craftById[rate.craft_id]?.name ?? rate.craft_id.slice(0, 8)}</TableCell>
                  <TableCell align="right">{fmtMoney(rate.base_hourly_rate)}/hr</TableCell>
                  <TableCell align="right">{fmtMoney(rate.fringe_hourly)}/hr</TableCell>
                  <TableCell>{rate.effective_start}</TableCell>
                  <TableCell>{rate.effective_end ?? "—"}</TableCell>
                  <TableCell>
                    <Tooltip title="Delete rate">
                      <IconButton size="small" color="error" onClick={() => deleteRateMut.mutate(rate.id)}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Paper>

      {/* OT Rule Assignment */}
      <Paper variant="outlined" sx={{ p: 3, mb: 3 }}>
        <Typography variant="subtitle1" fontWeight={600} mb={2}>OT Rule Assignment</Typography>
        {otAssignment ? (
          <Box>
            <Typography variant="body2">
              Current: {otRuleById[otAssignment.ot_rule_set_id]?.name ?? otAssignment.ot_rule_set_id}
              {" "}(effective {otAssignment.effective_start})
            </Typography>
          </Box>
        ) : (
          <Typography variant="body2" color="text.secondary">No OT rule set assigned.</Typography>
        )}
        <Box sx={{ mt: 2, display: "flex", gap: 2, alignItems: "center" }}>
          <TextField
            select
            label="Assign Rule Set"
            size="small"
            sx={{ width: 300 }}
            onChange={(e) => e.target.value && assignOTMut.mutate(e.target.value)}
          >
            {otRuleSets.map((r) => (
              <MenuItem key={r.id} value={r.id}>{r.name}</MenuItem>
            ))}
          </TextField>
        </Box>
      </Paper>

      {/* Retro Pay Preview */}
      <Paper variant="outlined" sx={{ p: 3 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
          <AutoAwesomeIcon color="warning" fontSize="small" />
          <Typography variant="subtitle1" fontWeight={600}>Retro Pay Preview</Typography>
        </Box>
        <Typography variant="body2" color="text.secondary" mb={2}>
          Preview backpay if a rate changed mid-period.
        </Typography>
        <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 2, mb: 2 }}>
          <MoneyField
            label="Old Rate / hr"
            size="small"
            value={retroForm.old_rate}
            onChange={(v) => setRetroForm((p) => ({ ...p, old_rate: v }))}
          />
          <MoneyField
            label="New Rate / hr"
            size="small"
            value={retroForm.new_rate}
            onChange={(v) => setRetroForm((p) => ({ ...p, new_rate: v }))}
          />
          <TextField
            label="Effective Date"
            size="small"
            type="date"
            value={retroForm.effective_date}
            onChange={(e) => setRetroForm((p) => ({ ...p, effective_date: e.target.value }))}
            InputLabelProps={{ shrink: true }}
          />
        </Box>
        <Button
          variant="outlined"
          color="warning"
          onClick={() => retroPreviewMut.mutate()}
          disabled={!retroForm.old_rate || !retroForm.new_rate || !retroForm.effective_date || retroPreviewMut.isPending}
          startIcon={retroPreviewMut.isPending ? <CircularProgress size={16} /> : <AutoAwesomeIcon />}
        >
          {retroPreviewMut.isPending ? "Calculating…" : "Preview Retro Pay"}
        </Button>

        {retroPreview && (
          <Box sx={{ mt: 2 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Date</TableCell>
                  <TableCell align="right">Hours</TableCell>
                  <TableCell align="right">Old Amount</TableCell>
                  <TableCell align="right">New Amount</TableCell>
                  <TableCell align="right">Delta</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {retroPreview.map((row, i) => (
                  <TableRow key={i}>
                    <TableCell>{row.work_date}</TableCell>
                    <TableCell align="right">{row.hours}</TableCell>
                    <TableCell align="right">{fmtMoney(row.old_amount)}</TableCell>
                    <TableCell align="right">{fmtMoney(row.new_amount)}</TableCell>
                    <TableCell align="right" sx={{ color: parseFloat(row.delta) >= 0 ? "success.main" : "error.main", fontWeight: 600 }}>
                      {parseFloat(row.delta) >= 0 ? "+" : ""}{fmtMoney(row.delta)}
                    </TableCell>
                  </TableRow>
                ))}
                <TableRow>
                  <TableCell colSpan={4} align="right" sx={{ fontWeight: 700 }}>Total Retro:</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700, color: retroTotal >= 0 ? "success.main" : "error.main" }}>
                    {retroTotal >= 0 ? "+" : ""}{fmtMoney(retroTotal)}
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </Box>
        )}
      </Paper>

      {/* Add rate slide-over */}
      <Drawer anchor="right" open={addRateOpen} onClose={() => setAddRateOpen(false)}>
        <Box sx={{ width: 400, p: 3, display: "flex", flexDirection: "column", gap: 2, mt: 2 }}>
          <Typography variant="h6" fontWeight={600}>Add Craft Rate</Typography>
          <Divider />
          <TextField
            select
            label="Craft"
            value={rateForm.craft_id}
            onChange={(e) => setRateForm((p) => ({ ...p, craft_id: e.target.value }))}
            required
          >
            {crafts.map((c) => (
              <MenuItem key={c.id} value={c.id}>{c.name} ({c.code})</MenuItem>
            ))}
          </TextField>
          <MoneyField
            label="Base Hourly Rate"
            value={rateForm.base_hourly_rate}
            onChange={(v) => setRateForm((p) => ({ ...p, base_hourly_rate: v }))}
            required
          />
          <MoneyField
            label="Fringe Hourly"
            value={rateForm.fringe_hourly}
            onChange={(v) => setRateForm((p) => ({ ...p, fringe_hourly: v }))}
          />
          <TextField
            label="Effective Start"
            type="date"
            value={rateForm.effective_start}
            onChange={(e) => setRateForm((p) => ({ ...p, effective_start: e.target.value }))}
            InputLabelProps={{ shrink: true }}
            required
          />
          <TextField
            label="Effective End (optional)"
            type="date"
            value={rateForm.effective_end}
            onChange={(e) => setRateForm((p) => ({ ...p, effective_end: e.target.value }))}
            InputLabelProps={{ shrink: true }}
          />
          {createRateMut.isError && (
            <Alert severity="error">{String((createRateMut.error as Error)?.message)}</Alert>
          )}
          <Box sx={{ display: "flex", gap: 2, mt: 1 }}>
            <Button onClick={() => setAddRateOpen(false)}>Cancel</Button>
            <Button
              variant="contained"
              disabled={!rateForm.craft_id || !rateForm.base_hourly_rate || createRateMut.isPending}
              onClick={() => createRateMut.mutate()}
            >
              {createRateMut.isPending ? "Saving…" : "Save Rate"}
            </Button>
          </Box>
        </Box>
      </Drawer>
    </Box>
  );
}
